/**
 * The two views that read live metrics.
 *
 * `MetricReading` is the bridge between a catalog result and a Reading: it carries the
 * server's own provenance and staleness through to the card, so the freshness a person sees
 * is the one the server measured, not the time the browser happened to receive it.
 */

import { Reading, type ReadingState } from '../Reading';
import { formatValue, getTestRuns, type ApiFailure, type MetricResult } from '../api';
import { peak, total, useMetric } from '../useMetric';
import { usePoll } from '../usePoll';
import { secs } from '../video-format';
import { central } from '../time';
import { TestTrafficNotice } from './LoadTests';

type Reduce = 'total' | 'peak' | 'single';

export function MetricReading({
  id, label, reduce = 'total', environment = 'production', suffixFromLabel,
}: {
  id: string;
  label: string;
  reduce?: Reduce;
  environment?: string;
  /** Label key to append, e.g. the node or volume a peak belongs to. */
  suffixFromLabel?: string;
}) {
  const { result, failure, loading } = useMetric(id);

  if (loading && !result) {
    return <Reading label={label} state="unknown" source="loading" reason="Reading from Prometheus." />;
  }

  // A failure leaves the previous value on screen but stops calling it current.
  if (failure && !result) {
    return (
      <Reading label={label} state={failure.kind === 'unavailable' ? 'error' : 'unknown'}
        source="operations API"
        reason={'reason' in failure ? failure.reason : 'Could not be read.'} />
    );
  }
  if (!result) return <Reading label={label} state="unknown" source="operations API" reason="No result." />;

  const picked = pick(result, reduce);
  if (picked === undefined) {
    // Query ran, returned nothing. That is not zero — it means nothing matched.
    return (
      <Reading label={label} state="unknown" source={result.source}
        observedAt={new Date(result.observedAt)} environment={environment}
        reason="The query returned no series." />
    );
  }

  const { value, unit } = formatValue(picked.value, result.unit);
  const state: ReadingState = failure ? 'stale' : result.stale ? 'stale' : 'live';
  const suffix = suffixFromLabel ? picked.labels[suffixFromLabel] : undefined;

  return (
    <Reading
      label={label}
      value={value}
      unit={unit}
      state={state}
      source={result.source}
      observedAt={new Date(result.observedAt)}
      environment={environment}
      reason={[
        suffix ? `highest: ${suffix}` : undefined,
        result.truncated ? `${result.truncated} more series not shown` : undefined,
        failure ? 'Last refresh failed; this value is no longer current.' : undefined,
      ].filter(Boolean).join(' · ') || undefined}
    />
  );
}

function pick(m: MetricResult, reduce: Reduce) {
  if (reduce === 'peak') return peak(m);
  if (reduce === 'single') return m.samples[0];
  const t = total(m);
  return t === undefined ? undefined : { value: t, labels: {} };
}

/* ------------------------------------------------------------ infrastructure */

export function InfrastructureLive() {
  return (
    <>
      <div className="readings">
        <MetricReading id="scrape.up" label="Scrape targets up" reduce="single" />
        <MetricReading id="container.cpu" label="Production CPU" reduce="total" />
        <MetricReading id="container.memory" label="Production memory" reduce="total" />
        <MetricReading id="container.restarts24h" label="Restarts, 24h" reduce="total" />
        <MetricReading id="node.cpuUsed" label="Busiest node CPU" reduce="peak" suffixFromLabel="instance" />
        <MetricReading id="node.memoryUsed" label="Busiest node memory" reduce="peak" suffixFromLabel="instance" />
        <MetricReading id="volume.used" label="Fullest volume" reduce="peak" suffixFromLabel="persistentvolumeclaim" />
        <MetricReading id="workload.ready" label="Replicas available" reduce="total" />
      </div>

      <div className="notice">
        <h3>Replica count is not proof of scalability</h3>
        <p>
          PeerTube runs one replica. Raising that is not a safe operation until singleton jobs,
          shared storage and plugin behaviour have been checked — this view reports the count
          and will not recommend changing it.
        </p>
      </div>
    </>
  );
}

/* ----------------------------------------------------------------- database */

export function DatabaseLive() {
  return (
    <>
      <div className="readings">
        <MetricReading id="pg.connections" label="Postgres connections" reduce="total" />
        <MetricReading id="pg.databaseSize" label="Largest database" reduce="peak" suffixFromLabel="datname" />
        <MetricReading id="pg.xidAge" label="Transaction ID age" reduce="peak" suffixFromLabel="datname" />
        <MetricReading id="pg.replicationLag" label="Replication lag" reduce="single" />
        <MetricReading id="pg.longestTransaction" label="Longest transaction" reduce="peak" />
        <MetricReading id="redis.memoryUsedRatio" label="Redis memory used" reduce="single" />
        <MetricReading id="redis.clients" label="Redis clients" reduce="single" />
        <MetricReading id="redis.keys" label="Redis keys" reduce="single" />
      </div>

      <div className="notice">
        <h3>Redis holds the job queues, so its memory ceiling is a correctness limit</h3>
        <p>
          The policy is <code>noeviction</code>: at the ceiling Redis refuses a write rather
          than discarding a queued transcode to make room. That turns a full cache into a
          visible error instead of silently lost work, which is why the ratio matters more
          than the absolute figure. Persistence is AOF on a dedicated volume, added on
          23 September, and 2,062 queue keys survived the restart that installed this exporter.
        </p>
      </div>

      <div className="notice">
        <h3>Transaction ID age and long transactions belong together</h3>
        <p>
          Postgres stops accepting writes as transaction IDs approach two billion. A long idle
          transaction blocks vacuum, which is what drives that number up — so the two are read
          side by side rather than in separate sections.
        </p>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ traffic */

export function TrafficLive() {
  return (
    <>
      <TestTrafficNotice />
      <div className="readings">
        <MetricReading id="http.requestRate" label="HTTP requests" reduce="single" />
        <MetricReading id="http.latencyP95" label="Request latency p95" reduce="single" />
        <MetricReading id="http.errorRate" label="5xx share" reduce="single" />
        <MetricReading id="http.byHost" label="Busiest host" reduce="peak" suffixFromLabel="host" />
        <MetricReading id="http.cacheHitRatio" label="Edge cache hit rate" reduce="single" />
        <MetricReading id="users.active" label="Active accounts" reduce="single" />
        <MetricReading id="viewers.current" label="Viewers watching now" reduce="single" />
      </div>
      <div className="notice blue">
        <h3>Requests and viewers are different measurements</h3>
        <p>
          Requests per second comes from the ingress controller. Viewers watching now comes
          from PeerTube playback telemetry. They count different things and will not agree —
          neither is derived from the other.
        </p>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------- video */

/** Playback start: never measured live, only in media load-test runs. */
function PlaybackStart() {
  const runs = usePoll(getTestRuns, 60_000);
  const run = runs.data?.runs.find((r) => Number.isFinite(r.video?.startP95S) && r.endedAt);
  const note = run
    ? `Measured only in load-test runs. Newest finished media run (#${run.id}, ${central(run.endedAt!)}): start p95 ${secs(run.video!.startP95S)}.`
    : 'Measured only in load-test runs.';
  return <Reading label="Playback start time" state="unknown" source="load tests" reason={note} />;
}

export function VideoLive() {
  return (
    <>
      <div className="readings">
        <MetricReading id="viewers.current" label="Viewers watching now" reduce="single" />
        <MetricReading id="lives.running" label="Live streams" reduce="single" />
        <MetricReading id="videos.total" label="Videos" reduce="single" />
        <MetricReading id="video.storageBytes" label="Video stored" reduce="single" />
        <MetricReading id="playback.errors24h" label="Playback errors, 24 h" reduce="single" />
        <MetricReading id="playback.stalls24h" label="Buffer stalls, 24 h" reduce="single" />
        <MetricReading id="playback.qualitySwitches24h" label="Quality switches, 24 h" reduce="single" />
        <MetricReading id="playback.httpBytes24h" label="Video bytes from object storage, 24 h" reduce="single" />
        <MetricReading id="playback.p2pBytes24h" label="P2P bytes, 24 h" reduce="single" />
        <MetricReading id="playback.p2pShare24h" label="P2P share" reduce="single" />
        <PlaybackStart />
        <Reading label="Video CDN hit ratio" state="unknown" source="no CDN"
          reason="Video streams straight from the object-storage bucket; CDN not live yet. The page and API edge cache is on Traffic & audience." />
      </div>
      <div className="notice">
        <h3>Playback problems come from the player</h3>
        <p>
          Errors, stalls and quality switches are counted by viewers' players and sent back to
          PeerTube, so they are what viewers actually experienced. Time to first frame is the one
          playback number nothing reports yet.
        </p>
        <p>P2P is enabled but carries nothing at current viewer overlap, so a P2P reading of 0 is real.</p>
      </div>
    </>
  );
}

/* -------------------------------------------------------------------- jobs */

export function JobsLive() {
  return (
    <>
      <div className="readings">
        <MetricReading id="jobs.pending" label="Jobs in flight" reduce="total" />
        <MetricReading id="jobs.waitingByType" label="Largest waiting queue" reduce="peak" suffixFromLabel="jobType" />
        <MetricReading id="jobs.failed" label="Failed jobs retained" reduce="single" />
        <MetricReading id="runners.count" label="Remote runners" reduce="single" />
      </div>
      <div className="notice">
        <h3>Queue age matters more than queue depth</h3>
        <p>
          A long queue that is moving is healthy; a short one that has not moved in twenty
          minutes is not. PeerTube reports depth and state but not the age of the oldest job,
          so this view shows what exists rather than inventing the number that does not.
        </p>
      </div>
    </>
  );
}

/** Shared failure banner for a view whose metrics cannot be reached at all. */
export function MetricsUnavailable({ failure }: { failure: ApiFailure }) {
  return (
    <div className="notice red">
      <h3>Metrics cannot be read</h3>
      <p>{'reason' in failure ? failure.reason : 'The Operations API did not respond.'}</p>
    </div>
  );
}

/* --------------------------------------------------------------------- vapr */

export function VaprLive() {
  return (
    <>
      <h3>Ad serving</h3>
      <div className="readings">
        <MetricReading id="vapr.adsP95" label="Ad request p95" reduce="single" />
        <MetricReading id="vapr.adsLastHour" label="Ad requests, last hour" reduce="single" />
        <MetricReading id="vapr.scoredShare" label="Answered by scored matches" reduce="single" />
        <MetricReading id="vapr.adsErrorShare" label="Ad requests that failed" reduce="single" />
        <MetricReading id="vapr.stepMean" label="Slowest matching step" reduce="peak" suffixFromLabel="step" />
      </div>

      <h3>Product feeds and scoring</h3>
      <div className="readings">
        <MetricReading id="vapr.feedProducts" label="Products available" reduce="single" />
        <MetricReading id="vapr.feedAge" label="Stalest feed update" reduce="single" />
        <MetricReading id="vapr.scoringOldestQueued" label="Oldest scoring job waiting" reduce="single" />
        <MetricReading id="vapr.claudeFailShare" label="Scoring calls failed, 24 h" reduce="single" />
      </div>

      <div className="notice">
        <h3>A failed ad request looks like an empty one</h3>
        <p>
          advid catches its own errors and serves no ads, so viewers see an empty slot rather than an
          error page. The failure share is the only place that shows. Scoring failures are different:
          they stop new videos being matched, and existing matches keep serving.
        </p>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ security */

export function SecurityLive() {
  return (
    <>
      <h3>Databases</h3>
      <div className="readings">
        <MetricReading id="security.pgBackupAge" label="Oldest Postgres backup" reduce="peak" suffixFromLabel="namespace" />
        <MetricReading id="security.pgBackupFailing" label="Clusters whose last backup failed" reduce="single" />
        <MetricReading id="security.pgRecoveryWindow" label="Point-in-time recovery reaches back" reduce="single" />
        <MetricReading id="security.walBacklog" label="WAL files waiting to archive" reduce="single" />
      </div>

      <h3>Volumes and certificates</h3>
      <div className="readings">
        <MetricReading id="security.dataBackupAge" label="Production /data backup" reduce="single" />
        <MetricReading id="security.certSoonest" label="Next certificate expiry" reduce="single" suffixFromLabel="name" />
        <MetricReading id="security.certsNotReady" label="Certificates not ready" reduce="single" />
      </div>

      <div className="notice">
        <h3>A backup is proven only by a restore</h3>
        <p>
          Production Postgres was restored from its backups in a drill on 6 October 2026: 4 min 39 s,
          losing 22 seconds of writes, every check matching production
          (<code>runbooks/postgres-restore-drill.md</code>). The staging /data volume restored from its
          Longhorn backup in 50 minutes on 28 September (<code>runbooks/restore-drill.md</code>).
          Staging Postgres has no backups configured; its data is test data.
        </p>
      </div>
    </>
  );
}
